import { useMemo } from "react";

import { useFormikWithDirtyForm } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addHours, roundToNearestMinutes } from "date-fns";
import * as Yup from "yup";

import { ConfigureBannerRequest, bannersApi } from "@/api";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { BannerConfigurationFormValues, BannerConfigurationMaxLength } from "../BannerConfiguration.types";

type BannerConfigurationValidationType = {
  [Property in keyof BannerConfigurationFormValues]?: Yup.AnySchema | Yup.Reference;
};

export function useBannerConfiguration() {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const queryClient = useQueryClient();

  const { data: activeBanner, isLoading } = useQuery({
    queryKey: [QueryKey.MaintenanceBanner],
    queryFn: () => bannersApi.getActiveBanner(),
  });

  const configureMutation = useMutation({
    mutationFn: (request: ConfigureBannerRequest) => bannersApi.configureBanner({ configureBannerRequest: request }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.MaintenanceBanner] });
      showSuccess(t("settings.banner.configuration.saveSuccess"));
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: () => bannersApi.deactivateBanner(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.MaintenanceBanner] });
      showSuccess(t("settings.banner.configuration.deactivateSuccess"));
    },
  });

  const initialValues = useMemo((): BannerConfigurationFormValues => {
    if (activeBanner) {
      return {
        bannerStartDate: activeBanner.bannerStartUtc,
        maintenanceStartDate: activeBanner.maintenanceStartUtc,
        maintenanceEndDate: activeBanner.maintenanceEndUtc,
        message: activeBanner.message ?? "",
      };
    }

    const defaultMaintenanceStart = roundToNearestMinutes(new Date(), {
      nearestTo: 30,
      roundingMethod: "ceil",
    });

    return {
      bannerStartDate: new Date(),
      maintenanceStartDate: defaultMaintenanceStart,
      maintenanceEndDate: addHours(defaultMaintenanceStart, 2),
      message: "",
    };
  }, [activeBanner]);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape<BannerConfigurationValidationType>({
        bannerStartDate: Yup.date()
          .nullable()
          .required(
            t("common.validation.fieldRequired", { fieldName: t("settings.banner.configuration.bannerStart") })
          ),
        maintenanceStartDate: Yup.date()
          .nullable()
          .required(
            t("common.validation.fieldRequired", { fieldName: t("settings.banner.configuration.maintenanceStart") })
          )
          .test(
            "maintenance-start-after-banner",
            t("settings.banner.configuration.validation.bannerStartAfterMaintenance"),
            function (maintenanceStart) {
              const { bannerStartDate } = this.parent as BannerConfigurationFormValues;
              if (!bannerStartDate) {
                return true;
              }
              return maintenanceStart > bannerStartDate;
            }
          ),
        maintenanceEndDate: Yup.date()
          .nullable()
          .required(
            t("common.validation.fieldRequired", { fieldName: t("settings.banner.configuration.maintenanceEnd") })
          )
          .test(
            "end-after-start",
            t("settings.banner.configuration.validation.maintenanceEndBeforeStart"),
            function (maintenanceEnd) {
              const { maintenanceStartDate } = this.parent as BannerConfigurationFormValues;
              if (!maintenanceStartDate) {
                return true;
              }
              return maintenanceEnd > maintenanceStartDate;
            }
          ),
        message: Yup.string().max(BannerConfigurationMaxLength.message, ({ max }) =>
          t("common.validation.maxLength", { max })
        ),
      }),
    [t]
  );

  const formik = useFormikWithDirtyForm<BannerConfigurationFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      if (!values.bannerStartDate || !values.maintenanceStartDate || !values.maintenanceEndDate) {
        return;
      }

      const request: ConfigureBannerRequest = {
        bannerStartUtc: values.bannerStartDate,
        maintenanceStartUtc: values.maintenanceStartDate,
        maintenanceEndUtc: values.maintenanceEndDate,
        message: values.message || null,
      };

      try {
        await configureMutation.mutateAsync(request);
      } catch {
        // Reported by the global error handling; the form keeps the input for a retry.
      }
    },
  });

  const handleDeactivate = () => {
    deactivateMutation.mutate();
  };

  return {
    formik,
    activeBanner,
    isLoading,
    handleDeactivate,
    isDeactivating: deactivateMutation.isPending,
    messageMaxLength: BannerConfigurationMaxLength.message,
  };
}
